<template>
  <div class="tw-min-h-[calc(100vh-60px)] tw-bg-white tw-py-10">
    <div class="tw-mx-auto tw-w-full tw-max-w-7xl tw-px-6 lg:tw-px-8">

      <div class="tw-mb-10">
        <h1 class="tw-text-2xl tw-font-bold tw-text-slate-900">Reports & Analytics</h1>
        <p class="tw-mt-1 tw-text-sm tw-text-slate-500">
          Generate insights on yard operations, inventory, and efficiency.
        </p>
      </div>

      <div class="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-grid-cols-2 lg:tw-grid-cols-3">
        <div v-for="type in reportTypes" :key="type.id"
          class="tw-relative tw-flex tw-flex-col tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-p-6 tw-shadow-sm tw-transition-shadow hover:tw-shadow-md">

          <div class="tw-flex tw-items-start tw-justify-between tw-mb-4">
            <div
              :class="[type.iconBg, type.iconColor, 'tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-xl']">
              <font-awesome-icon :icon="type.icon" class="tw-h-5 tw-w-5" />
            </div>

            <div class="tw-relative">
              <button @click.stop="togglePastReports(type.id)"
                class="tw-p-2 tw-text-slate-400 hover:tw-text-slate-600 tw-transition-colors">
                <font-awesome-icon icon="ellipsis-vertical" />
              </button>

              <div v-if="openPastReportsId === type.id" v-click-outside="() => openPastReportsId = null"
                class="tw-absolute tw-right-0 tw-z-10 tw-mt-2 tw-w-72 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-2 tw-shadow-xl tw-ring-1 tw-ring-black/5">

                <div
                  class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-slate-400">
                  <font-awesome-icon icon="clock-rotate-left" /> Past reports
                </div>

                <div v-if="type.pastReports && type.pastReports.length">
                  <div v-for="report in type.pastReports" :key="report.id"
                    class="tw-group tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-px-3 tw-py-2 hover:tw-bg-slate-50">
                    <div class="tw-min-w-0">
                      <div class="tw-truncate tw-text-xs tw-font-medium tw-text-slate-700">{{ report.displayName }}
                      </div>
                      <div class="tw-text-[10px] tw-text-slate-400">{{ report.fileFormat }} * Ready</div>
                    </div>
                    <button @click="downloadPastReport(report)"
                      class="tw-p-1 tw-text-slate-400 hover:tw-text-blue-600 tw-transition-colors">
                      <font-awesome-icon icon="download" class="tw-h-3.5 tw-w-3.5" />
                    </button>
                  </div>
                </div>

                <div v-else class="tw-px-3 tw-py-4 tw-text-center tw-text-xs tw-text-slate-400 italic">
                  No recent reports
                </div>
              </div>
            </div>
          </div>

          <h3 class="tw-text-lg tw-font-bold tw-text-slate-900">{{ type.name }}</h3>
          <p class="tw-mt-2 tw-text-sm tw-leading-relaxed tw-text-slate-500 tw-flex-grow">
            {{ type.description }}
          </p>

          <button @click="openReportGenerator(type.id)"
            class="tw-mt-6 tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-bg-slate-50 tw-py-3 tw-text-sm tw-font-semibold tw-text-slate-700 tw-transition hover:tw-bg-slate-100">
            <font-awesome-icon icon="chart-simple" class="tw-h-3.5 tw-w-3.5" />
            {{ type.id === 'DOC_BUILDER' ? 'Manage Templates' : 'Generate Report' }}
          </button>
        </div>

        <!-- Static copy of the last report type (DOC_BUILDER) -->
        <div class="tw-relative tw-flex tw-flex-col tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-p-6 tw-shadow-sm tw-transition-shadow hover:tw-shadow-md">

          <div class="tw-flex tw-items-start tw-justify-between tw-mb-4">
            <div
              class="tw-bg-orange-50 tw-text-orange-600 tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-xl">
              <font-awesome-icon icon="file-signature" class="tw-h-5 tw-w-5" />
            </div>
          </div>

          <h3 class="tw-text-lg tw-font-bold tw-text-slate-900">Document Templates</h3>
          <p class="tw-mt-2 tw-text-sm tw-leading-relaxed tw-text-slate-500 tw-flex-grow">
            Create and manage dynamic PDF templates. Drag and drop data fields onto your dispatch sheets and BOLs.
          </p>

          <button @click="openDocBuilder()"
            class="tw-mt-6 tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-bg-slate-50 tw-py-3 tw-text-sm tw-font-semibold tw-text-slate-700 tw-transition hover:tw-bg-slate-100">
            <font-awesome-icon icon="chart-simple" class="tw-h-3.5 tw-w-3.5" />
            Manage Templates
          </button>
        </div>
      </div>
    </div>

    <Modal v-model:isOpen="isModalOpen" modalId="reports-generate-modal" maxWidth="md">
      <template #title>Generate {{ activeReportName }}</template>
      <template #subtitle>Fill in the information below to generate your report</template>
      <template #content>
        <div class="tw-space-y-6">

        <div v-if="reportType !== 'ORDER'" class="tw-space-y-4">
          <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Date Range</label>
          <div class="tw-grid tw-grid-cols-2 tw-gap-4">
            <div>
              <span class="tw-mb-1 tw-block tw-text-[10px] tw-font-bold tw-uppercase tw-text-slate-400">From</span>
              <DatePicker v-model="dateStart" />
              <p v-if="formErrors.dateStart" class="tw-mt-1 tw-text-xs tw-text-rose-600">{{ formErrors.dateStart }}</p>
            </div>
            <div>
              <span class="tw-mb-1 tw-block tw-text-[10px] tw-font-bold tw-uppercase tw-text-slate-400">To</span>
              <DatePicker v-model="dateEnd" />
              <p v-if="formErrors.dateEnd" class="tw-mt-1 tw-text-xs tw-text-rose-600">{{ formErrors.dateEnd }}</p>
            </div>
          </div>
          <p v-if="formErrors.dateRange" class="tw-mt-1 tw-text-xs tw-text-rose-600">{{ formErrors.dateRange }}</p>
        </div>

        <div v-if="reportType === 'MOVES'" class="tw-space-y-4">
          <div class="tw-grid tw-grid-cols-2 tw-gap-4">
            <div>
              <InputField v-model="driverId" label="Driver ID" type="number" placeholder="ID" />
              <p v-if="formErrors.driverId" class="tw-mt-1 tw-text-xs tw-text-rose-600">{{ formErrors.driverId }}</p>
            </div>
            <div>
              <InputField v-model="chassis" label="Chassis Number" placeholder="Chassis #" />
              <p v-if="formErrors.chassis" class="tw-mt-1 tw-text-xs tw-text-rose-600">{{ formErrors.chassis }}</p>
            </div>
          </div>
          <div class="tw-grid tw-grid-cols-2 tw-gap-4">
            <div>
              <InputField v-model="originId" label="Origin ID" type="number" />
              <p v-if="formErrors.originId" class="tw-mt-1 tw-text-xs tw-text-rose-600">{{ formErrors.originId }}</p>
            </div>
            <div>
              <InputField v-model="destinationId" label="Destination ID" type="number" />
              <p v-if="formErrors.destinationId" class="tw-mt-1 tw-text-xs tw-text-rose-600">{{ formErrors.destinationId }}</p>
            </div>
          </div>
          <DropdownList v-model="weekStartDay" :options="['MONDAY', 'SATURDAY']" label="Week Starts On" />
        </div>

        <div v-if="reportType === 'ORDER' || reportType === 'MOVES'">
          <InputField v-model="orderId" label="Booking / Reference Number" placeholder="e.g. EBKG12345678" />
          <p v-if="formErrors.orderId" class="tw-mt-1 tw-text-xs tw-text-rose-600">{{ formErrors.orderId }}</p>
        </div>

        <div v-if="reportType === 'DRIVERS_TRIPS'">
          <button 
            class="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-px-3 tw-py-2 "
            :class="[driversTripsDateMode === 'EXACT_DATES' ? 'tw-bg-slate-800 tw-text-white' : 'tw-bg-slate-100 tw-text-slate-400']"
            @click="driversTripsDateMode = 'EXACT_DATES'"
          >Exact Dates</button>
          <button 
            class="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-px-3 tw-py-2 "
            :class="[driversTripsDateMode === 'WEEKS' ? 'tw-bg-slate-800 tw-text-white' : 'tw-bg-slate-100 tw-text-slate-400']"
            @click="driversTripsDateMode = 'WEEKS'"
          >Weeks</button>
          <label class="tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer">
            <input 
              type="checkbox" 
              v-model="driversTripsByDriversSwitch"
              class="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300"
            /> 
            print by driver
          </label>
          <div v-if="driversTripsDateMode === 'WEEKS'">
            <FilterableDropdown
              v-model="driversTripsWeekSelected"
              class="tw-w-full tw-min-w-0"
              :options="weekOptions"
              label="formatted"
              :inPill="true"
              @input="(week) => { const vm = this as any; vm.dateStart = week.start; vm.dateEnd = week.end}"
            ></FilterableDropdown>
          </div>

        </div>
        <div v-if="reportType === 'TRACKING'">
          <DropdownList v-model="status" label="Filter by Status"
            :options="['All', 'Onboard Vessel', 'Available', 'Not available', 'Off-Dock']" />
        </div>

        <div class="tw-border-t tw-border-slate-100 tw-pt-5">
          <label class="tw-mb-3 tw-block tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-slate-400">
            Export as
          </label>
          <div class="tw-grid tw-grid-cols-3 tw-gap-3">
            <button v-for="fmt in (['PDF', 'XLSX', 'CSV'] as const)" :key="fmt" @click="selectedFormat = fmt" :class="[
              'tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-py-3 tw-text-sm tw-font-bold tw-transition',
              selectedFormat === fmt
                ? 'tw-border-slate-900 tw-bg-slate-900 tw-text-white'
                : 'tw-border-slate-200 tw-bg-white tw-text-slate-600 hover:tw-border-slate-300'
            ]">
              {{ fmt }}
            </button>
          </div>
        </div>

        <Button @click="handleGenerate" :loading="loading"
          class="tw-w-full tw-rounded-xl tw-bg-slate-900 tw-py-4 tw-text-white hover:tw-bg-slate-800 tw-flex tw-items-center tw-justify-center tw-gap-3">
          <font-awesome-icon v-if="loading" icon="circle-notch" spin />
          <font-awesome-icon v-else icon="download" />
          {{ loading ? 'Generating...' : 'Generate & Download' }}
        </Button>
        </div>
      </template>
    </Modal>
    <DocumentTemplateEditor v-if="isDocBuilderOpen" :is-open="isDocBuilderOpen" @close="isDocBuilderOpen = false"
       />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import GenerateReportMutation from '@/gql/reports/GenerateReport.gql';
import GetPastReportsQuery from '@/gql/reports/GetPastReports.gql';

import Modal from '@/components/core/Modal/Modal.vue';
import DatePicker from '@/components/core/DateTime/DatePicker.vue';
import DropdownList from '@/components/core/DropdownList.vue';
import InputField from '@/components/core/Input/InputField.vue';
import Button from '@/components/core/Button.vue';
import { pushToast } from '@/lib/toast';
import { useAuthStore } from '@/stores/auth';
import FilterableDropdown from '../core/FilterableDropdown.vue';

import DocumentTemplateEditor from './DocumentTemplateEditor.vue';

interface ReportWeekOption {
  id: number;
  start: Date;
  end: Date;
  formatted: string;
  isCurrent: boolean;
}

function generateWeeksForYear(weekStartDayInput: string | number = 1): ReportWeekOption[] {
  const dayNameToNumber: Record<string, number> = {
    SUNDAY: 1,
    MONDAY: 2,
    TUESDAY: 3,
    WEDNESDAY: 4,
    THURSDAY: 5,
    FRIDAY: 6,
    SATURDAY: 7,
  };

  const weekStartDay = typeof weekStartDayInput === 'number'
    ? weekStartDayInput
    : (dayNameToNumber[weekStartDayInput] || 1);

  const currentYear = new Date().getFullYear();
  const weeks: ReportWeekOption[] = [];

  // Convert 1-7 (1=Su) to JS day (0=Su)
  const jsWeekStartDay = weekStartDay - 1;

  // Find the first occurrence of the week start day in the current year
  let firstDay = new Date(currentYear, 0, 1);
  while (firstDay.getDay() !== jsWeekStartDay) {
    firstDay.setDate(firstDay.getDate() + 1);
  }

  // If the first week start day is not Jan 1, check if Jan 1's week starts in previous year
  // We only count weeks where the start day is within the current year
  let weekStart = new Date(firstDay);
  let weekIndex = 1;

  const now = new Date();
  while (weekStart.getFullYear() === currentYear) {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const weekEndEOD = new Date(weekEnd);
    weekEndEOD.setDate(weekEndEOD.getDate() + 1)

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const formatDate = (date: Date): string => {
      //const month = String(date.getMonth() + 1).padStart(2, '0');
      const month = monthNames[date.getMonth()]
      const day = String(date.getDate()).padStart(2, '0');
      return `${month} ${day}`;
    };

    weeks.push({
      id: weekIndex,
      start: new Date(weekStart),
      end: weekEnd,
      isCurrent: now >= weekStart && now < weekEndEOD,
      formatted: `Week #${weekIndex} (${formatDate(weekStart)} - ${formatDate(weekEnd)})`
    });

    weekStart.setDate(weekStart.getDate() + 7);
    weekIndex++;
  }

  return weeks;
}

export default defineComponent({
  name: 'ReportsPage',
  components: { Modal, DatePicker, DropdownList, InputField, Button, FilterableDropdown, DocumentTemplateEditor },
  data() {
    return {
      isModalOpen: false,
      openPastReportsId: null as string | null,
      loading: false,
      formErrors: {} as Record<string, string>,
      selectedFormat: 'PDF' as 'PDF' | 'CSV' | 'XLSX',
      reportType: 'TRACKING' as 'ORDER' | 'TRACKING' | 'MOVES' | 'DRIVERS_TRIPS',
      isDocBuilderOpen: false,

      reportTypes: [
        {
          id: 'TRACKING',
          name: 'Container Tracking',
          icon: 'location-dot',
          iconBg: 'tw-bg-blue-50',
          iconColor: 'tw-text-blue-600',
          description: 'Track current container status, locations, and vessel availability within a selected date range.',
          pastReports: [],
        },
        {
          id: 'MOVES',
          name: 'Container Moves',
          icon: 'ship',
          iconBg: 'tw-bg-green-50',
          iconColor: 'tw-text-green-600',
          description: 'Analyze container moves by driver, chassis, or origin/destination with flexible week-start configurations.',
          pastReports: [],
        },
        {
          id: 'ORDER',
          name: 'Order Details',
          icon: 'file-invoice',
          iconBg: 'tw-bg-indigo-50',
          iconColor: 'tw-text-indigo-600',
          description: 'Generate a comprehensive report of all containers and logistics details associated with a specific booking number.',
          pastReports: []
        },
        {
          id: 'DRIVERS_TRIPS',
          name: "Drivers Trips Details",
          icon: 'truck',
          iconBg: 'tw-bg-indigo-50',
          iconColor: 'tw-text-indigo-600',
          description: 'Generate a report of all drivers trips in a given period',
          pastReports: [],
          weekStartDay: 'SATURDAY'
        }
      ],

      dateStart: '',
      dateEnd: '',
      driversTripsByDriversSwitch: false,
      driversTripsDateMode: 'WEEKS' as 'WEEKS' | 'EXACT_DATES',
      driversTripsWeekSelected: 'none' as string,
      weekStartDay: 'MONDAY',
      driverId: null as number | null,
      originId: null as number | null,
      destinationId: null as number | null,
      chassis: '',
      orderId: '',
      status: 'All',
    };
  },
  watch: {
    reportType() {
      (this as any).formErrors = {};
    },
    isModalOpen(val: boolean) {
      if (val) (this as any).formErrors = {};
    },
    dateStart() {
      const vm = this as any;
      delete vm.formErrors.dateStart;
      delete vm.formErrors.dateRange;
    },
    dateEnd() {
      const vm = this as any;
      delete vm.formErrors.dateEnd;
      delete vm.formErrors.dateRange;
    },
    orderId() {
      const vm = this as any;
      delete vm.formErrors.orderId;
    },
    driverId() {
      const vm = this as any;
      delete vm.formErrors.driverId;
    },
    originId() {
      const vm = this as any;
      delete vm.formErrors.originId;
    },
    destinationId() {
      const vm = this as any;
      delete vm.formErrors.destinationId;
    },
    chassis() {
      const vm = this as any;
      delete vm.formErrors.chassis;
    },
    weekOptions() {
      if (!this.driversTripsWeekSelected) {
        this.driversTripsWeekSelected = (this.weekOptions.find(w => w.isCurrent) || {}).formatted || 'none';
      }
    }
  },
  mounted() {
    document.addEventListener('keydown', (this as any).onKeyDown, true);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', (this as any).onKeyDown, true);
  },
  computed: {
    activeReportName() {
      return this.reportTypes.find(t => t.id === this.reportType)?.name || '';
    },
    // either the general week-start day OR the week-start specific to the current report type
    reportTypeWeekStartDay(): string {
      const reportConfig = this.reportTypes.find(r => r.id === this.reportType);
      return String((reportConfig && reportConfig.weekStartDay) || this.weekStartDay || 'MONDAY');
    },
    weekOptions(): ReportWeekOption[] {
      return generateWeeksForYear(this.reportTypeWeekStartDay);
    }
  },
  methods: {
    toDate(val: any): Date | null {
      if (!val) return null;
      if (val instanceof Date && !isNaN(val.getTime())) return val;
      if (typeof val === 'string') {
        const d = new Date(val);
        return isNaN(d.getTime()) ? null : d;
      }
      return null;
    },
    validateGenerateInputs(): boolean {
      const vm = this as any;

      vm.formErrors = {};

      const type = vm.reportType;

      if (type === 'ORDER') {
        const order = String(vm.orderId || '').trim();
        if (!order) {
          vm.formErrors.orderId = 'Booking / Reference number is required.';
        }
      }

      if (type === 'TRACKING' || type === 'MOVES') {
        const start = vm.toDate(vm.dateStart);
        const end = vm.toDate(vm.dateEnd);

        if (!start || !end) {
          if (!start) vm.formErrors.dateStart = 'Start date is required.';
          if (!end) vm.formErrors.dateEnd = 'End date is required.';
        }

        if (start && end && start.getTime() > end.getTime()) {
          vm.formErrors.dateRange = 'From date must be before To date.';
        }
      }

      if (type === 'MOVES') {
        const numFields = [
          { label: 'Driver ID', value: vm.driverId },
          { label: 'Origin ID', value: vm.originId },
          { label: 'Destination ID', value: vm.destinationId },
        ];

        for (const f of numFields) {
          if (f.value === null || f.value === undefined || f.value === '') continue;
          const n = Number(f.value);
          if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) {
            if (f.label === 'Driver ID') vm.formErrors.driverId = 'Must be a positive whole number.';
            if (f.label === 'Origin ID') vm.formErrors.originId = 'Must be a positive whole number.';
            if (f.label === 'Destination ID') vm.formErrors.destinationId = 'Must be a positive whole number.';
          }
        }

        if (vm.chassis && String(vm.chassis).trim().length < 3) {
          vm.formErrors.chassis = 'Chassis number looks too short.';
        }
      }

      return Object.keys(vm.formErrors).length === 0;
    },
    onKeyDown(e: KeyboardEvent) {
      const vm = this as any;
      if (!vm.openPastReportsId) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        vm.openPastReportsId = null;
      }
    },
    async togglePastReports(id: string) {
      if (this.openPastReportsId === id) {
        this.openPastReportsId = null;
        return;
      }

      this.openPastReportsId = id;
      await this.fetchPastReports(id);
    },

    async fetchPastReports(reportType: string) {
      try {
        const { data } = await this.$apollo.query({
          query: GetPastReportsQuery,
          variables: { reportType },
          fetchPolicy: 'network-only'
        });

        const typeIndex = this.reportTypes.findIndex(t => t.id === reportType);
        if (typeIndex !== -1) {
          this.reportTypes[typeIndex].pastReports = data.pastReports;
        }
      } catch (e) {
        console.error("Failed to fetch history", e);
      }
    },

    async downloadPastReport(report) {
      try {
        const token = useAuthStore().token;

        if (!token) {
          pushToast("Session expired or token missing. Please log in.", "error");
          return;
        }

        const authHeader = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

        console.log("Downloading with token key: jwt_access_token");

        const response = await fetch(report.downloadUrl, {
          method: 'GET',
          headers: {
            'Authorization': authHeader,
          },
        });

        if (!response.ok) {
          if (response.status === 401) throw new Error("Unauthorized: Invalid token");
          if (response.status === 403) throw new Error("Forbidden: This is not your company's report");
          throw new Error(`Download failed: ${response.status}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const extension = report.fileFormat.toLowerCase();
        link.download = `${report.displayName}.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        pushToast("Download started", "success");
      } catch (e) {
        console.error("Download Error:", e);
        pushToast("error", "error");
      }
    },

    openDocBuilder() {
        this.isDocBuilderOpen = true;
        this.openPastReportsId = null;
    },
    openReportGenerator(id: any) {
      this.reportType = id;
      this.isModalOpen = true;
      this.openPastReportsId = null;
    },
    async handleGenerate() {
      const typeToUpdate = this.reportType;
      const ok = await this.generate(this.selectedFormat);
      if (ok) {
        this.isModalOpen = false;
        await this.fetchPastReports(typeToUpdate);
      }
    },


    base64ToBlob(base64: string, type: string) {
      const binStr = atob(base64);
      const len = binStr.length;
      const arr = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        arr[i] = binStr.charCodeAt(i);
      }
      return new Blob([arr], { type: type });
    },
    async generate(format: 'PDF' | 'CSV' | 'XLSX'): Promise<boolean> {
      if (!(this as any).validateGenerateInputs()) return false;

      this.loading = true;

      try {
        const { data } = await this.$apollo.mutate({
          mutation: GenerateReportMutation,
          variables: {
            reportType: this.reportType,
            outputFormat: format,
            dateStart: this.dateStart,
            dateEnd: this.dateEnd,
            weekStartDay: this.reportTypeWeekStartDay,
            driverId: this.driverId ? parseInt(this.driverId.toString()) : null,
            originId: this.originId ? parseInt(this.originId.toString()) : null,
            destinationId: this.destinationId ? parseInt(this.destinationId.toString()) : null,
            chassis: this.chassis,
            status: this.status,
            orderNumber: this.orderId,
            isPrintByDriver: this.driversTripsByDriversSwitch
          },
        });

        const report = data?.generateReport?.report;

        if (report?.success && report?.base64Content) {
          let mimeType = 'application/pdf';
          if (format === 'CSV') mimeType = 'text/csv';
          if (format === 'XLSX') mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

          const blob = this.base64ToBlob(report.base64Content, mimeType);
          const url = window.URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = url;
          link.download = report.filename;
          document.body.appendChild(link);
          link.click();

          window.URL.revokeObjectURL(url);
          document.body.removeChild(link);

          pushToast("Report downloaded successfully", "success");
          return true;
        } else {
          pushToast("No data found for the selected filters", "warning");
          return false;
        }
      } catch (e: any) {
        const gqlMessage = e?.graphQLErrors?.[0]?.message;
        const networkMessage = e?.networkError?.message;
        const message = gqlMessage || networkMessage || e?.message;

        if (typeof message === 'string' && message.includes('No containers found')) {
          pushToast('No containers found for the selected filters.', 'warning');
          return false;
        } else {
          console.error("Report generation failed", e);
          pushToast(message || "An error occurred", "error");
          return false;
        }
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>

<style scoped>
:deep(.uk-button-default) {
  @apply tw-w-full tw-rounded-lg tw-border-slate-200 tw-text-sm tw-text-left tw-flex tw-justify-between tw-items-center;
  text-transform: none;
  height: 42px;
}
</style>
